import CheckCircle from "@mui/icons-material/CheckCircle";
import CircleOutlined from "@mui/icons-material/CircleOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Pending from "@mui/icons-material/Pending";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  Stack,
  Skeleton,
  SxProps,
  Switch,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { mdHeight, xsHeight } from "components/AppBar";
import {
  GetWasteShipmentQuery,
  Issue,
  IssueLevel,
  UnitType,
  WasteShipmentStatus,
} from "generated-graphql/graphql";
import pluralize from "pluralize";
import { Link as RouterLink } from "react-router-dom";

import { formatQuantity, NO_VALUE_PLACEHOLDER } from "../Containers/containerDisplay";
import { missingManifestTotalReason } from "./reconciliationDisplay";
import { formatShipmentDate, formatTotalsByUnit, groupLinesByStream } from "./shipmentDisplay";
import { LifecycleState, LifecycleStepKey, lifecycleSteps } from "./shipmentLifecycle";
import { ShipmentReconciliationChip } from "./ShipmentReconciliationChip";
import { WasteShipmentStatusChip } from "./WasteShipmentStatusChip";

type Shipment = NonNullable<GetWasteShipmentQuery["wasteShipment"]>;

/** Marker size, matching the spec's 20px glyph in the progress track. */
const STEP_ICON_SIZE = 20;

function StepIcon({ state }: { state: LifecycleState }) {
  if (state === "done") {
    return <CheckCircle color="success" sx={{ fontSize: STEP_ICON_SIZE }} />;
  }
  if (state === "current") {
    // Amber and mid-motion: the step the shipment is sitting on, as distinct
    // from one already closed out.
    return <Pending color="warning" sx={{ fontSize: STEP_ICON_SIZE }} />;
  }
  if (state === "inactive") {
    // A step that will never happen, as distinct from one that has not happened
    // yet — a shipment arranged by phone never produces a request document. The
    // same dash Scout's todo list uses for a skipped item.
    return <RemoveCircleOutline sx={{ fontSize: STEP_ICON_SIZE, color: "text.disabled" }} />;
  }
  if (state === "terminal") {
    // A dashed ring rather than a filled or empty one: the step was reached
    // and closed, but not completed.
    return (
      <Box
        sx={{
          width: STEP_ICON_SIZE,
          height: STEP_ICON_SIZE,
          borderRadius: "50%",
          border: "2px dashed",
          borderColor: "text.disabled",
        }}
      />
    );
  }
  return <CircleOutlined sx={{ fontSize: STEP_ICON_SIZE, color: "text.disabled" }} />;
}

/**
 * Identity and alarms.
 *
 * One chip: where the shipment itself has got to. The paperwork's verdict is
 * independent of that — a shipment can ship without a manifest and reconcile
 * weeks later — but it now states itself in `ShipmentInformationCard`, beside
 * the two totals it is a verdict about. Repeating it here gave the same fact two
 * homes and made the header look like it carried two statuses.
 *
 * The callouts sit here rather than beside whatever caused them because both
 * a header field and a container can be the cause. A stale request means the
 * vendor is holding a document that no longer describes the load, which is
 * worth interrupting for.
 */
export function ShipmentHeader({
  shipment,
  issues,
  actions,
  overflow,
  onResendRequest,
  resendBlockedReason,
  requestStale = false,
}: {
  shipment: Shipment;
  issues: Issue[];
  /** Save / discard / unsaved-count, supplied by the page while editable. */
  actions?: React.ReactNode;
  overflow?: React.ReactNode;
  onResendRequest?: () => void;
  /**
   * Why re-sending is unavailable, or undefined when it is. The request is
   * built from saved state, so offering it mid-edit would hand the vendor a
   * document that does not match what the user is looking at.
   */
  resendBlockedReason?: string;
  /**
   * Whether the vendor is holding a request that no longer describes the load.
   *
   * The page decides, because it is not simply `manifestRequestStale`: once a
   * shipment has shipped or been canceled the vendor's copy cannot be brought
   * back into line, so saying it is out of date only invites an action that no
   * longer exists.
   */
  requestStale?: boolean;
}) {
  const blocking = issues.filter((issue) => issue.level === IssueLevel.Critical);

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" spacing={1.5} useFlexGap flexWrap="wrap">
        <Box sx={{ flexGrow: 1, minWidth: 220 }}>
          <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
            <Typography component="h1" variant="h5">
              {shipment.manifestRequestNumber}
            </Typography>
            <WasteShipmentStatusChip status={shipment.status} />
            {shipment.backfilledFromManifest && (
              <Typography variant="caption" color="text.secondary">
                Backfilled from manifest
              </Typography>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {/*
              The facility alone. The vendor and destination are request
              fields, and they have one home -- the Details card in the body,
              which states them whether or not the shipment can still be
              edited.
            */}
            {shipment.facility?.name || NO_VALUE_PLACEHOLDER}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
          {actions}
          {overflow}
        </Stack>
      </Stack>

      {blocking.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle sx={{ mb: 0.25 }}>
            {blocking.length} {pluralize("issue", blocking.length)}{" "}
            {blocking.length === 1 ? "blocks" : "block"} shipping
          </AlertTitle>
          {blocking.map((issue, index) => (
            <Typography key={`${issue.key}-${index}`} variant="body2">
              {issue.message}
            </Typography>
          ))}
        </Alert>
      )}

      {requestStale && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={
            onResendRequest ? (
              <Tooltip title={resendBlockedReason ?? ""}>
                <span>
                  <Button
                    size="small"
                    onClick={onResendRequest}
                    disabled={Boolean(resendBlockedReason)}
                  >
                    Send updated request
                  </Button>
                </span>
              </Tooltip>
            ) : undefined
          }
        >
          <AlertTitle sx={{ mb: 0.25 }}>The vendor&apos;s copy is out of date</AlertTitle>
          <Typography variant="body2">
            This shipment&apos;s contents changed after the request document was produced, so the
            vendor is holding a copy that no longer describes what is shipping.
          </Typography>
        </Alert>
      )}
    </Box>
  );
}

/** One tinted figure panel: a quiet label over the number it names. */
function MetricPanel({
  label,
  children,
  sx,
}: {
  label: string;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box sx={{ bgcolor: "grey.50", borderRadius: 1, px: 2, py: 1, ...sx }}>
      <Typography variant="caption" color="text.secondary" component="p">
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * The headline numbers, above the detail that explains them.
 *
 * The requested and manifest totals share one panel, split by a rule, because
 * they are the two halves of a single comparison — the question the page is open
 * to answer. The verdict chip sits on that rule rather than up in the page
 * header, so it reads as a statement about those two numbers and not as a second
 * status for the shipment.
 *
 * Counts sit apart from the comparison: they describe the load either way and do
 * not change when a manifest lands.
 */
export function ShipmentInformationCard({ shipment }: { shipment: Shipment }) {
  const containerCount = shipment.lines.length;
  const streamCount = groupLinesByStream(shipment.lines).length;
  const attached = shipment.manifestReceivedAt != null;

  // Taken from the server's total rather than summed from the quantity diffs:
  // diffs exist only where the two sides disagree, so a matching manifest would
  // have shown an em dash and a partly-matching one would have shown only the
  // differing streams' weight.
  const manifestTotal = shipment.reconciliation.manifestTotalPounds;

  return (
    <Paper elevation={0} sx={{ px: 3, py: 2, mb: 2.5 }}>
      <Typography
        component="p"
        variant="overline"
        color="text.secondary"
        sx={{ m: 0, mb: 1, letterSpacing: "1px" }}
      >
        Shipment information
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <MetricPanel
            label={attached ? "Requested amount" : "Total amount"}
            sx={{ height: "100%" }}
          >
            <Typography variant="h5" component="p">
              {formatTotalsByUnit(shipment.totalQuantityByUnit)}
            </Typography>
            {/* Only once there is a manifest to compare against. Before that the
                panel is one figure, not a comparison with an empty side. */}
            {attached && (
              <>
                <Divider sx={{ my: 1 }} />
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" component="p">
                      Manifest amount
                    </Typography>
                    {manifestTotal == null ? (
                      // Why there is no number, not an empty row. A bare label
                      // reads as a spacing gap, and the dangling case has no
                      // chip beside it to explain the blank either.
                      <Typography variant="body2" color="text.disabled" fontStyle="italic">
                        {missingManifestTotalReason(shipment)}
                      </Typography>
                    ) : (
                      <Typography variant="body1" color="text.secondary">
                        {formatQuantity(manifestTotal, UnitType.Pounds)}
                      </Typography>
                    )}
                  </Box>
                  <ShipmentReconciliationChip status={shipment.reconciliation.status} />
                </Stack>
              </>
            )}
          </MetricPanel>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <MetricPanel label={pluralize("Waste stream", streamCount)}>
              <Typography variant="h5" component="p">
                {streamCount}
              </Typography>
            </MetricPanel>
            <MetricPanel label={pluralize("Container", containerCount)}>
              <Typography variant="h5" component="p">
                {containerCount}
              </Typography>
            </MetricPanel>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

/**
 * The page's outer surface: a canvas the section cards sit on.
 *
 * Shared by the loaded page and its skeleton so the two cannot drift — a
 * skeleton on a different background is a visible flash at the moment the data
 * lands, which is exactly when the user is looking.
 */
export function ShipmentPageShell({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  return (
    <Stack
      spacing={3}
      sx={{
        py: 2,
        px: 3,
        backgroundColor: "canvas",
        // Fill below the app bar, or the canvas stops where the content does
        // and the page ends in a band of white.
        minHeight: {
          xs: `calc(100vh - ${theme.spacing(xsHeight)})`,
          md: `calc(100vh - ${theme.spacing(mdHeight)})`,
        },
      }}
    >
      {children}
    </Stack>
  );
}

/**
 * The two-column body: the rail leading, contents beside it, and the rail above
 * them when narrow.
 *
 * The rail is on the left because progress is what the page is read in order of
 * — where the shipment has got to, then what it holds. That also makes its
 * placement consistent across widths: it comes first at every size rather than
 * jumping from above the contents to their right.
 *
 * `body` stays first in the DOM. Grid areas place the rail visually without
 * moving the page's main content behind it in reading order.
 */
export function ShipmentPageGrid({ body, rail }: { body: React.ReactNode; rail: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 3,
        alignItems: "start",
        gridTemplateColumns: { xs: "1fr", md: "240px minmax(0, 1fr)", lg: "290px minmax(0, 1fr)" },
        gridTemplateAreas: { xs: '"rail" "body"', md: '"rail body"' },
      }}
    >
      <Stack spacing={3} sx={{ gridArea: "body", minWidth: 0 }}>
        {body}
      </Stack>
      <Box sx={{ gridArea: "rail", minWidth: 0 }}>{rail}</Box>
    </Box>
  );
}

/**
 * The page's shape before its data arrives.
 *
 * Deliberately the same shell, the same grid and the same card rhythm as the
 * loaded page: a skeleton that guesses at a different layout makes the content
 * jump into place rather than resolve.
 */
export function ShipmentDetailSkeleton() {
  const card = (children: React.ReactNode) => (
    <Paper elevation={0} sx={{ p: 3 }}>
      {children}
    </Paper>
  );
  return (
    <ShipmentPageShell>
      <Box>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Skeleton variant="text" width={130} height={36} />
          <Skeleton variant="rounded" width={78} height={24} />
        </Stack>
        <Skeleton variant="text" width={220} height={20} sx={{ mt: 0.5 }} />
      </Box>

      {/* Shipment information: the same card and panel grid as the real one, so
          the figures resolve in place instead of the block changing shape. */}
      <Paper elevation={0} sx={{ px: 3, py: 2 }}>
        <Skeleton variant="text" width={140} height={16} sx={{ mb: 1 }} />
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rounded" height={116} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={2}>
              <Skeleton variant="rounded" height={50} />
              <Skeleton variant="rounded" height={50} />
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <ShipmentPageGrid
        body={
          <>
            {card(
              <>
                <Skeleton variant="text" width={90} height={28} sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Skeleton variant="text" width={90} height={14} />
                      <Skeleton variant="text" width={140} height={20} />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
            {card(
              <>
                <Skeleton variant="text" width={140} height={28} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={120} />
              </>
            )}
          </>
        }
        rail={
          <Stack spacing={2}>
            {[4, 3].map((rows, cardIndex) => (
              <Paper key={cardIndex} elevation={0} sx={{ p: 3 }}>
                <Skeleton variant="text" width={84} height={16} sx={{ mb: 1.5 }} />
                <Stack spacing={1.75}>
                  {Array.from({ length: rows }).map((_, i) => (
                    <Box key={i}>
                      <Skeleton variant="text" width={72} height={13} />
                      <Skeleton variant="text" width={128} height={18} />
                    </Box>
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>
        }
      />
    </ShipmentPageShell>
  );
}

/**
 * The shipment's state, beside the contents rather than above them.
 *
 * Progress carries the next step, because the next step IS the progress —
 * keeping them together is what lets the body be about the waste. The
 * request's own fields are deliberately not here: they live in the body's
 * Details card in every status, so they never move as the shipment ages.
 */
export function ShipmentRail({
  shipment,
  stepActions,
  manifest,
}: {
  shipment: Shipment;
  /**
   * The action that advances a given step, rendered beneath that step's own
   * label. Keyed rather than passed as one block: an action detached from the
   * step it advances leaves the reader to work out which is which, and the
   * order of two buttons in a row does not say it.
   */
  stepActions?: Partial<Record<LifecycleStepKey, React.ReactNode>>;
  /** The manifest card, supplied by the page because it owns the dialogs. */
  manifest?: React.ReactNode;
}) {
  const steps = lifecycleSteps(shipment);

  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography
          component="p"
          variant="overline"
          color="text.secondary"
          sx={{ m: 0, mb: 2, letterSpacing: "1px" }}
        >
          Progress
        </Typography>
        <Stack>
          {steps.map((step, index) => {
            const action = stepActions?.[step.key];
            const last = index === steps.length - 1;
            return (
              <Stack key={step.key} direction="row" spacing={1} alignItems="stretch">
                {/* Icon over a hairline that reaches the next step's icon, so the
                    column reads as one track rather than four loose markers. The
                    last step has nothing to connect to. */}
                <Stack alignItems="center" sx={{ flexShrink: 0 }}>
                  <StepIcon state={step.state} />
                  {!last && (
                    <Box sx={{ flexGrow: 1, width: "1px", bgcolor: "divider", minHeight: 8 }} />
                  )}
                </Stack>
                <Box sx={{ minWidth: 0, pb: last ? 0 : 2 }}>
                  <Typography
                    variant="subtitle2"
                    color={
                      // Only a step that happened, or is happening, is stated at
                      // full strength. A skipped one used to render bolder than a
                      // pending one, which read as the more important of the two.
                      step.state === "done" || step.state === "current"
                        ? "text.primary"
                        : "text.disabled"
                    }
                  >
                    {step.label}
                  </Typography>
                  {step.meta && (
                    <Typography variant="caption" color="text.secondary" component="p">
                      {step.meta}
                    </Typography>
                  )}
                  {action && <Box sx={{ mt: 1 }}>{action}</Box>}
                </Box>
              </Stack>
            );
          })}
        </Stack>
      </Paper>

      {manifest}
    </Stack>
  );
}

/**
 * No manifest yet. Upload is asynchronous — the copy is extracted and run
 * through the pipeline before a manifest record exists — so the copy says so
 * rather than implying the file lands here directly.
 */
function ManifestRailEmpty({
  canceled,
  onLink,
  onUpload,
}: {
  canceled: boolean;
  onLink: () => void;
  onUpload: () => void;
}) {
  return (
    <Stack spacing={1.5} alignItems="flex-start">
      <Typography variant="body2" color="text.secondary">
        {canceled
          ? "This shipment was canceled, so no manifest can be attached."
          : "None yet. Link one Encamp has already received, or upload the vendor's copy — it is parsed first, then appears here to link."}
      </Typography>
      {!canceled && (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={onLink}>
            Link
          </Button>
          <Button size="small" onClick={onUpload}>
            Upload
          </Button>
        </Stack>
      )}
    </Stack>
  );
}

/**
 * The manifest as a fact about the shipment, not a section of the page.
 *
 * Its identity and dates sit in the rail with the rest of the shipment's state;
 * the comparison it produces lives on the waste streams it judges, in Contents.
 * Splitting them that way is the point — the numbers belong beside the lines
 * they describe, and the paperwork's identity belongs with the other metadata.
 */
export function ShipmentManifestRailCard({
  shipment,
  attaching,
  manifestHref,
  onLink,
  onUpload,
  onRelink,
  onDetach,
  onSignedChange,
}: {
  shipment: Shipment;
  attaching: boolean;
  /**
   * The manifest's own page. Null while the link dangles — there is a tracking
   * number to show from the snapshot, but no row to open.
   */
  manifestHref: string | null;
  onLink: () => void;
  onUpload: () => void;
  onRelink: () => void;
  onDetach: () => void;
  onSignedChange: (signed: boolean) => void;
}) {
  const attached = shipment.manifestReceivedAt != null;
  const dangling = attached && shipment.manifest == null;
  const trackingNumber =
    shipment.reconciliation.linkedManifestTrackingNumber ??
    shipment.manifest?.manifestTrackingNumber ??
    null;
  const canceled = shipment.status === WasteShipmentStatus.Canceled;

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Typography
        component="p"
        variant="overline"
        color="text.secondary"
        sx={{ m: 0, mb: 2, letterSpacing: "1px" }}
      >
        Manifest
      </Typography>

      {!attached ? (
        <ManifestRailEmpty canceled={canceled} onLink={onLink} onUpload={onUpload} />
      ) : (
        <Stack spacing={1.75}>
          <RailFact label="Tracking number" value={trackingNumber} href={manifestHref} />
          <RailFact label="Received" value={formatShipmentDate(shipment.manifestReceivedAt)} />
          <RailFact
            label="Manifest ship date"
            value={formatShipmentDate(shipment.manifest?.shippedDate)}
          />
          {dangling && (
            <Typography variant="caption" color="text.secondary">
              Record rebuilding — the link repairs itself on the next manifest run.
            </Typography>
          )}
          {!canceled && (
            <>
              <Tooltip
                title={
                  dangling ? "Available once the manifest record has finished rebuilding." : ""
                }
              >
                <span>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={shipment.manifestSigned}
                        disabled={attaching || dangling}
                        onChange={(e) => onSignedChange(e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">Signed copy</Typography>}
                  />
                </span>
              </Tooltip>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={onRelink}>
                  Replace
                </Button>
                <Button size="small" color="inherit" onClick={onDetach}>
                  Detach
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      )}
    </Paper>
  );
}

/**
 * Label above value, not label-left and value-right.
 *
 * The rail is narrow enough that a facility or transporter name wraps, and a
 * right-aligned value that wraps leaves its label stranded against a ragged
 * two-line block. Stacking reads the same way the progress steps above it
 * already do, and it cannot break on a long name.
 */
function RailFact({
  label,
  value,
  href,
}: {
  label: string;
  value: string | null | undefined;
  /** Renders the value as a link. Ignored when there is no value to link. */
  href?: string | null;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {value && href ? (
        <Link
          component={RouterLink}
          to={href}
          // New tab, per the ModelLinks convention the contents rows follow:
          // the manifest is a side-trip from the shipment, not a destination.
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          {value}
          <OpenInNew sx={{ fontSize: 14 }} />
        </Link>
      ) : (
        <Typography variant="body2">{value || NO_VALUE_PLACEHOLDER}</Typography>
      )}
    </Box>
  );
}
